import { toDirectedGraph } from '@xstate/graph';
import { useMachine } from '@xstate/react';
import { FormikHelpers } from 'formik';
import { assign, isNil, partial } from 'lodash';
import { AnimatePresence, LazyMotion, domAnimation } from 'motion/react';
import * as m from 'motion/react-m';
import { FC, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import { FINANCE_RESIDENCY_STATUS, FinanceQuote } from 'src/data/FinanceApi/FinanceApi.types';
import { ProfileApiResponse } from 'src/data/ProfileApi/ProfileApi.types';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { CheckoutFooter } from 'src/general/components/CheckoutFooter/CheckoutFooterLegacy';
import { FormLayout } from 'src/general/components/FormLayout/FormLayout';
import { PersonalizedQuoteHeader } from 'src/general/components/PersonalizedQuoteHeader/PersonalizedQuoteHeader';
import { useCarmaError } from 'src/stores/errorStore';
import { liveChatStore } from 'src/stores/liveChatStore';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { PushToDataLayerConfig, pushToDataLayer } from 'src/utils/pushToDataLayer';

import { StateOrderMappings, getPersonalisedQuoteProgress } from './PersonalisedQuote.helpers';
import {
  MACHINE_EVENT,
  MACHINE_STATE,
  PersonalisedQuoteMachineContext,
  PersonalisedQuoteMachineInitialState,
  personalisedQuoteMachine,
} from './PersonalisedQuote.machine';
import { Confirmation } from './templates/Forms/Confirmation';
import { EmailForm } from './templates/Forms/EmailForm';
import { EmploymentCitizenshipForm } from './templates/Forms/EmploymentCitizenshipForm';
import { FamilyResidentialForm } from './templates/Forms/FamilyResidentialForm';
import { FinanceQuotesForm } from './templates/Forms/FinanceQuotesForm';
import { LoanDetailsForm } from './templates/Forms/LoanDetailsForm';
import { LoanTypeForm } from './templates/Forms/LoanTypeForm';
import { PersonalDetailsForm } from './templates/Forms/PersonalDetailsForm';
import {
  EmailFormValues,
  EmploymentCitizenshipFormValues,
  FamilyResidentialFormValues,
  LoanDetailsFormValues,
  LoanTypeFormValues,
  PersonalDetailsFormValues,
  PersonalisedFinanceQuotesFormValues,
  PersonalisedQuotePayloadPartial,
  PersonalisedQuotePayloadPaymentOptionFinanceQuote,
  PersonalizedQuoteFormProps,
  PersonalizedQuoteFormValues,
  ResendPasswordFormValues,
  ResetPasswordFormValues,
  SigninFormValues,
  SignupFormValues,
  TermsAndConditionsFormValues,
  VerificationTokenFormValues,
} from './templates/Forms/PersonalizedQuoteForm.type';
import { ResendPasswordForm } from './templates/Forms/ResendPasswordForm';
import { ResetPasswordForm } from './templates/Forms/ResetPasswordForm';
import { SignInForm } from './templates/Forms/SignInForm';
import { SignupForm } from './templates/Forms/SignupForm';
import { TermsAndConditionsForm } from './templates/Forms/TermsAndConditions';
import { VerificationTokenForm } from './templates/Forms/VerificationTokenForm';

const TRANSLATION_DISTANCE = 120;
const DELAY = 250;

const transitionVariants = {
  enter: (reverse: boolean) => {
    return {
      y: reverse ? -TRANSLATION_DISTANCE : TRANSLATION_DISTANCE,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    y: 0,
    opacity: 1,
  },
  exit: (reverse: boolean) => {
    return {
      zIndex: 0,
      y: reverse ? TRANSLATION_DISTANCE : -TRANSLATION_DISTANCE,
      opacity: 0,
    };
  },
};

const scrollTop = () => {
  if (globalThis.window !== undefined) {
    window.scrollTo(0, 0);
  }
};

interface Step<T extends PersonalizedQuoteFormValues> {
  stepState: MACHINE_STATE;
  Step: FC<PersonalizedQuoteFormProps<T>>;
  getValues: (context: PersonalisedQuoteMachineContext) => PersonalizedQuoteFormProps<T>['values'];
  onSubmit: (
    currentState: MACHINE_STATE,
    context: PersonalisedQuoteMachineContext,
    values: T,
    formikHelpers: FormikHelpers<T>,
  ) => Promise<true | string>;
  onBack?: PersonalizedQuoteFormProps<T>['onBack'];
  setValuesPartial?: (
    currentState: MACHINE_STATE,
    context: PersonalisedQuoteMachineContext,
    values: PersonalisedQuotePayloadPartial,
    updateFinanceInformation?: boolean,
  ) => void;
  email?: PersonalizedQuoteFormProps<T>['email'];
  phone?: PersonalizedQuoteFormProps<T>['phone'];
  onResendTokenVerificationCode?: PersonalizedQuoteFormProps<T>['onResendTokenVerificationCode'];
  fetchCarsCount?: PersonalizedQuoteFormProps<T>['fetchCarsCount'];
  onClickResetPassword?: PersonalizedQuoteFormProps<T>['onClickResetPassword'];
  isLoggedIn?: PersonalizedQuoteFormProps<T>['isLoggedIn'];
}

interface Props {
  userData: ProfileApiResponse | null;
  /**
   * Optional initial machine state.
   * Determines at which state the machine should be at
   */
  initialMachineState?: PersonalisedQuoteMachineInitialState;
  /**
   * Called between every step of the personalised quote form
   * @returns PersonalisedQuotePayloadPartial if successful, string with error message otherwise
   */
  onUpdatePersonalisedQuote: (
    currentState: MACHINE_STATE,
    payload: PersonalisedQuotePayloadPartial,
  ) => Promise<PersonalisedQuotePayloadPartial | string>;
  /**
   * Called when submitting selected quotes
   * @returns updated list of quotes, string with error message otherwise
   */
  onConfirmFinanceQuote: (quoteRequestId: string, quoteId: string[]) => Promise<FinanceQuote[]>;
  fetchCarsCount: (buildYear: string, purchasePrice: string) => Promise<number>;
  onClickCarmaLogo: () => void;
  onCloseQuote: () => void;
  onSubmitEmail: (email: string) => Promise<boolean>;
  /**
   * Sign the user in
   * @returns `true` if successful, the error message otherwise
   */
  onSignin: (email: string, password: string) => Promise<true | string>;
  /**
   * Request a password reset
   * @returns `true` if successful, the error message otherwise
   */
  isLoggedIn: boolean;
  onResetPassword: (email: string) => Promise<true | string>;
  /**
   * Check if a phone number is taken
   * @returns `true` if number taken, `false` otherwise
   */
  onCheckPhoneNumberTaken: (phone: string) => Promise<boolean>;
  /**
   * Send a verification token
   * @returns the verification id
   */
  onSendVerificationToken: (phone: string) => Promise<string>;
  /**
   * Sign the user up
   * @returns `true` if successful, the error message otherwise
   */
  onSignup: (signupParams: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    password: string;
    marketing: boolean;
    acceptedTermsAndConditions: boolean;
    verificationId: string;
    verificationCode: string;
  }) => Promise<true | string>;
}

const TERMS_CONDITIONS_URL = '/terms-and-conditions';
const POLICY_URL = '/privacy-policy';
const PRIVACY_COLLECTION_NOTICE_URL = '/privacy-collection-notice';

export const PersonalisedQuote: FC<Props> = ({
  userData,
  onUpdatePersonalisedQuote,
  onConfirmFinanceQuote,
  initialMachineState,
  onClickCarmaLogo,
  onCloseQuote,
  fetchCarsCount,
  onSubmitEmail,
  onSignin,
  onResetPassword,
  onCheckPhoneNumberTaken,
  onSendVerificationToken,
  onSignup,
  isLoggedIn,
}) => {
  const getErrorMessage = useCarmaError();
  const { visibility, hideLiveChat, maximiseLiveChat } = liveChatStore(
    ({ visibility, hideLiveChat, maximiseLiveChat }) => ({
      visibility,
      hideLiveChat,
      maximiseLiveChat,
    }),
    shallow,
  );

  const [state, send, interpretService] = useMachine(personalisedQuoteMachine, {
    state: initialMachineState,
  });
  const onBack = () => {
    pushToDataLayer({
      event: `personalised_quote_click`,
      value: `back`,
      personalised_quote_referrer: `personalised_quote_step-${state.value}`,
    });
    send(MACHINE_EVENT.BACK);
  };
  const onSubmitTracking = (success: boolean = false, reasons?: string[]) =>
    pushToDataLayer({
      event: `personalised_quote_step-${state.value}-submitAttempt`,
      success,
      reasons,
    });

  const openLiveChat = () => {
    maximiseLiveChat();
    pushToDataLayer({
      event: `Personalized_quote_open_live_chat`,
    });
  };

  // Adjust the visibility if it's set to 'minimized'
  useEffect(() => {
    if (visibility === 'minimized') {
      hideLiveChat();
    }
  }, [visibility]);

  useEffect(() => {
    if (isLoggedIn && userData) {
      send(MACHINE_EVENT.LOGIN);
    }
  }, [send, isLoggedIn, userData]);

  const steps = [
    {
      stepState: MACHINE_STATE.START,
      Step: LoanDetailsForm,
      getValues: (context) => ({ ...context.payload.loanDetails }),
      onSubmit: async (currentState, _context, values) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, { loanDetails: values });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      fetchCarsCount,
    } as Step<LoanDetailsFormValues>,
    {
      stepState: MACHINE_STATE.LOAN_TYPE,
      Step: LoanTypeForm,
      getValues: (context) => context.payload.loanType,
      onSubmit: async (currentState, _context, values) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, { loanType: values });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          newPayload.loanType = values;
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
      isLoggedIn: isLoggedIn,
    } as Step<LoanTypeFormValues>,
    {
      stepState: MACHINE_STATE.EMAIL,
      Step: EmailForm,
      getValues: (context) => ({
        email: userData?.email ?? context.payload.email?.email,
      }),
      onSubmit: async (currentState, _context, values) => {
        const result = await onSubmitEmail(values.email);
        send({ type: MACHINE_EVENT.SEARCH_FOR_ACCOUNT, isExistingAccount: result });

        const newPayload = await onUpdatePersonalisedQuote(currentState, { email: values });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          newPayload.email = values;
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
    } as Step<EmailFormValues>,
    {
      stepState: MACHINE_STATE.SIGN_IN,
      Step: SignInForm,
      getValues: (context) => context.payload.signIn,
      onSubmit: async (currentState, context, values, formikHelpers) => {
        const result = await onSignin(context.payload.email?.email ?? '', values.password);
        if (result === true) {
          const newPayload = await onUpdatePersonalisedQuote(currentState, {});
          onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
          return newPayload;
        } else {
          const errorMessage = 'Invalid password';
          formikHelpers.setFieldError('password', errorMessage);
          onSubmitTracking(false, [`api: ${errorMessage}`]);
          return result;
        }
      },
      onBack,
      onClickResetPassword: () => {
        send(MACHINE_EVENT.RESET_PASSWORD);
      },
      email: state.context.payload.email?.email,
    } as Step<SigninFormValues>,
    {
      stepState: MACHINE_STATE.RESET_PASSWORD,
      Step: ResetPasswordForm,
      getValues: (context) => context.payload.email,
      onSubmit: async (currentState, _context, values, formikHelpers) => {
        const result = await onResetPassword(values.email);
        if (result === true) {
          const newPayload = await onUpdatePersonalisedQuote(currentState, {});
          onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
          if (typeof newPayload !== 'string') {
            send(MACHINE_EVENT.NEXT, { payload: newPayload });
          }
          return newPayload;
        } else {
          const errorMessage = 'Could not reset password';
          onSubmitTracking(false, [`api: ${errorMessage}`]);
          formikHelpers.setFieldError('password', errorMessage);
        }
      },
      onBack,
    } as Step<ResetPasswordFormValues>,
    {
      stepState: MACHINE_STATE.RESEND_PASSWORD,
      Step: ResendPasswordForm,
      getValues: (context) => context.payload.email,
      onSubmit: async (currentState, context, _values, formikHelpers) => {
        const result = await onResetPassword(context.payload.email?.email ?? '');

        if (result === true) {
          const newPayload = await onUpdatePersonalisedQuote(currentState, {});
          onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
          return newPayload;
        } else {
          const errorMessage = 'Could not reset password';
          onSubmitTracking(false, [`api: ${errorMessage}`]);
          formikHelpers.setFieldError('password', errorMessage);
        }
      },
      onBack,
      email: state.context.payload.email?.email,
    } as Step<ResendPasswordFormValues>,
    {
      stepState: MACHINE_STATE.PERSONAL_DETAILS,
      Step: PersonalDetailsForm,
      phone: userData?.phone,
      getValues: (context) => ({
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        phone: userData?.phone ?? context.payload.personalDetails?.phone,
        ...context.payload.personalDetails,
        isBusinessUseSelected: context.payload.isBusinessUseSelected,
      }),
      onSubmit: async (currentState, _context, values, formikHelpers) => {
        if (!userData?.phone) {
          const isPhoneTaken = await onCheckPhoneNumberTaken(values.phone);
          if (isPhoneTaken) {
            const errorMessage = getErrorMessage('ct_phone_already_in_use', 'This phone number is already taken');
            onSubmitTracking(false, [`api: ${errorMessage}`]);
            formikHelpers.setFieldError('phone', errorMessage);
            return errorMessage;
          }
        }
        const newPayload = await onUpdatePersonalisedQuote(currentState, {
          personalDetails: values,
        });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
    } as Step<PersonalDetailsFormValues>,
    {
      stepState: MACHINE_STATE.FAMILY_AND_RESIDENTIAL,
      Step: FamilyResidentialForm,
      getValues: (context) => ({
        ...context.payload.familyDetails,
        isBusinessUseSelected: context.payload.isBusinessUseSelected,
      }),
      onSubmit: async (currentState, _context, values) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, { familyDetails: values });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
    } as Step<FamilyResidentialFormValues>,
    {
      stepState: MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
      Step: EmploymentCitizenshipForm,
      getValues: (context) => ({
        ...context.payload.employmentAndCitizenshipDetails,
        isBusinessUseSelected: context.payload.isBusinessUseSelected,
      }),
      onSubmit: async (currentState, _context, values) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, { employmentAndCitizenshipDetails: values });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
      isLoggedIn: isLoggedIn,
    } as Step<EmploymentCitizenshipFormValues>,
    {
      stepState: MACHINE_STATE.TERMS_AND_CONDITIONS,
      Step: TermsAndConditionsForm,
      getValues: (context) => ({
        ...context.payload.termsAndConditionsDetails,
        isBusinessUseSelected: context.payload.isBusinessUseSelected,
      }),
      onSubmit: async (currentState, context, values) => {
        const emailValue = context.payload.email ? context.payload.email.email : userData?.email;
        const phoneValue = context.payload.personalDetails?.phone
          ? context.payload.personalDetails?.phone
          : userData?.phone;
        if (emailValue && phoneValue) {
          const newPayload = await onUpdatePersonalisedQuote(currentState, {
            loanDetails: context.payload.loanDetails,
            personalDetails: context.payload.personalDetails,
            familyDetails: context.payload.familyDetails,
            email: { email: emailValue },
            phone: { phone: phoneValue },
            employmentAndCitizenshipDetails: context.payload.employmentAndCitizenshipDetails,
            termsAndConditionsDetails: values,
            isBusinessUseSelected: context.payload.isBusinessUseSelected,
          });
          onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
          if (typeof newPayload !== 'string') {
            send(MACHINE_EVENT.NEXT, { payload: newPayload });
          }
          return newPayload;
        }
      },
      onBack,
    } as Step<TermsAndConditionsFormValues>,
    {
      stepState: MACHINE_STATE.CREATE_ACCOUNT,
      Step: SignupForm,
      getValues: (context) => context.payload.accountSignupDetails,
      onSubmit: async (currentState, context, values) => {
        const verificationId = await onSendVerificationToken(context.payload.phone?.phone ?? '');
        const newPayload = await onUpdatePersonalisedQuote(currentState, {});
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          newPayload.accountSignupDetails = { ...values, verificationId: verificationId };
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
      email: state.context.payload.email?.email,
      phone: state.context.payload.phone?.phone,
    } as Step<SignupFormValues>,
    {
      stepState: MACHINE_STATE.VERIFY_ACCOUNT,
      Step: VerificationTokenForm,
      getValues: (context) => context.payload.accountSignupVerification,
      onSubmit: async (currentState, context, values, formikHelpers) => {
        const emailValue = context.payload.email ? context.payload.email.email : userData?.email;
        const phoneValue = context.payload.phone ? context.payload.phone.phone : userData?.phone;

        if (context.payload.personalDetails && context.payload.accountSignupDetails && emailValue && phoneValue) {
          const result = await onSignup({
            email: emailValue,
            phone: phoneValue,
            firstName: context.payload.personalDetails?.firstName,
            lastName: context.payload.personalDetails?.lastName,
            password: context.payload.accountSignupDetails?.newPassword,
            marketing: !!context.payload.accountSignupDetails?.newsAndMarketing,
            acceptedTermsAndConditions: true,
            verificationId: context.payload.accountSignupDetails?.verificationId,
            verificationCode: values.verificationCode,
          });
          if (result === true) {
            send(MACHINE_EVENT.LOGIN);

            const newPayload = await onUpdatePersonalisedQuote(currentState, {
              loanDetails: context.payload.loanDetails,
              personalDetails: context.payload.personalDetails,
              familyDetails: context.payload.familyDetails,
              email: { email: emailValue },
              phone: { phone: phoneValue },
              employmentAndCitizenshipDetails: context.payload.employmentAndCitizenshipDetails,
              termsAndConditionsDetails: context.payload.termsAndConditionsDetails,
              isBusinessUseSelected: context.payload.isBusinessUseSelected,
            });

            onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
            if (typeof newPayload !== 'string') {
              send(MACHINE_EVENT.NEXT, { payload: newPayload });
            }
            return newPayload;
          } else {
            formikHelpers.setFieldError('verificationCode', result);
            onSubmitTracking(false, [`api: ${result}`]);
          }
        }
      },
      onBack,
      onResendTokenVerificationCode: async () => {
        if (state.context.payload.phone?.phone) {
          await onSendVerificationToken(state.context.payload.phone?.phone);
        }
      },
      phone: state.context.payload.phone?.phone,
    } as Step<VerificationTokenFormValues>,
    {
      stepState: MACHINE_STATE.FINANCE_QUOTES_LIST,
      Step: FinanceQuotesForm,
      getValues: (context) => ({
        ...context.payload.financeQuote,
        quoteRequestId: context.payload.financeQuoteRequest!.quoteRequestId,
        carPrice: context.payload.loanDetails?.estimatedPurchasePrice,
        deposit: context.payload.loanDetails?.depositAmount ?? 0,
        buyType: context.payload.loanType?.loanType,
        isVisaHolder:
          context.payload.employmentAndCitizenshipDetails?.residencyStatus ===
          FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT,
      }),
      onSubmit: async (currentState, context, values) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, {
          financeQuoteRequest: context.payload.financeQuoteRequest,
          financeQuote: values as PersonalisedQuotePayloadPaymentOptionFinanceQuote,
        });
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          if (values.quoteId) {
            await onConfirmFinanceQuote(values.quoteRequestId, values.quoteId);
          }
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
    } as Step<PersonalisedFinanceQuotesFormValues>,
    {
      stepState: MACHINE_STATE.CONFIRMATION_PAGE,
      Step: Confirmation,
      getValues: (context) => ({ isQuoteConfirmed: context.payload.isQuoteConfirmed }),
      onSubmit: async (currentState) => {
        const newPayload = await onUpdatePersonalisedQuote(currentState, {});
        onSubmitTracking(typeof newPayload !== 'string', typeof newPayload === 'string' ? [newPayload] : undefined);
        if (typeof newPayload !== 'string') {
          send(MACHINE_EVENT.NEXT, { payload: newPayload });
        }
        return newPayload;
      },
      onBack,
    } as Step<PersonalizedQuoteFormValues>,
  ];

  const currentStep = steps.find((step) => state.value === step.stepState);

  useEffect(() => {
    const subscription = interpretService.subscribe((state) => {
      let config: PushToDataLayerConfig | undefined = undefined;
      if (state.value === MACHINE_STATE.CONFIRMATION_PAGE) {
        config = {
          eventIdComponents: ['personal_quote_submit', new Date().toISOString()],
          sendToCDP: true,
        };
      }
      pushToDataLayer(
        {
          event: `personalised_quote_step-${state.value}`,
        },
        config,
      );
    });
    return subscription.unsubscribe;
  }, [interpretService]);

  const stateOrderMappings: StateOrderMappings = toDirectedGraph(personalisedQuoteMachine)
    .children.map((val) => ({
      [val.stateNode.key]: val.stateNode.order,
    }))
    .reduce(assign, {});

  return (
    <FormLayout
      header={
        <PersonalizedQuoteHeader
          progressStep={
            getPersonalisedQuoteProgress(
              state.context.payload,
              state.context.isLoggedIn,
              state.context.isExistingAccount,
              currentStep?.stepState,
              stateOrderMappings,
            ).progress
          }
          onClickCarmaLogo={onClickCarmaLogo}
          onCloseQuote={onCloseQuote}
          onContactCarma={openLiveChat}
        />
      }
      footer={
        <CheckoutFooter
          options={[
            {
              url: TERMS_CONDITIONS_URL,
              label: getMicrocopy(MICROCOPY.CHECKOUT_FOOTER_LINK_1),
            },
            {
              url: POLICY_URL,
              label: getMicrocopy(MICROCOPY.CHECKOUT_FOOTER_LINK_2),
            },
            {
              url: PRIVACY_COLLECTION_NOTICE_URL,
              label: 'Privacy collection notice',
            },
          ]}
        />
      }
    >
      <section data-testid={`state-${state.value}`}>
        <LazyMotion features={domAnimation}>
          <AnimatePresence initial={false} mode="wait" custom={state.context.isBackTransition}>
            {!isNil(currentStep) ? (
              <m.div
                key={`${currentStep.stepState}${state.context.isBackTransition ? '-reverse' : ''}`}
                custom={state.context.isBackTransition}
                variants={transitionVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ ease: 'easeInOut', duration: DELAY / 1000 }}
                onAnimationComplete={scrollTop}
              >
                <currentStep.Step
                  values={currentStep.getValues(state.context)}
                  setValuesPartial={
                    currentStep.setValuesPartial &&
                    partial(currentStep.setValuesPartial, state.value as MACHINE_STATE, state.context)
                  }
                  onSubmit={async (values, formikHelpers) =>
                    currentStep.onSubmit(
                      state.value as MACHINE_STATE,
                      state.context,
                      values as any,
                      formikHelpers as FormikHelpers<any>,
                    )
                  }
                  onSubmitTracking={onSubmitTracking}
                  onBack={currentStep.onBack}
                  fetchCarsCount={currentStep.fetchCarsCount}
                  onClickResetPassword={currentStep.onClickResetPassword}
                  isLoggedIn={isLoggedIn}
                  email={currentStep.email}
                  phone={currentStep.phone}
                />
              </m.div>
            ) : (
              <>
                <p>Step {state.value as MACHINE_STATE} not supported</p>
                <button onClick={onBack} type="button">
                  Back
                </button>
              </>
            )}
          </AnimatePresence>
        </LazyMotion>
      </section>
    </FormLayout>
  );
};
